import AppSettings from "../storage/appSettings";

/**
 * @fileoverview Utility functions for making GET and POST requests.
 * This module provides helper functions to interact with APIs.
 */

/**
 * Performs a GET request to the specified URL.
 * @async
 * @function getQuery
 * @param {string} url - The API endpoint to fetch data from.
 * @returns {Promise<{response: number, data?: any, error?: any}>}
 * An object containing the response status and either the data or an error message.
 */
export const getQuery = async (url) => {
  try {
    const response = await fetch(url, {
      method: "get",
    });

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      return { response: 400 };
    }

    const data = await response.json();
    AppSettings.setState({ connectionError: false });

    return {
      response: 200,
      data: data,
    };
  } catch (error) {
    console.log("Error fetching data:", error);
    AppSettings.setState({ connectionError: true });
    return { response: 400 };
  }
};

/**
 * Performs a POST request to execute a GraphQL query.
 * @async
 * @function postQuery
 * @param {string} url - The GraphQL endpoint to send the request to.
 * @param {string} query - The GraphQL query string.
 * @param {Object} [variables={}] - Optional variables for the GraphQL query.
 * @returns {Promise<{response: number, data?: any, error?: any}>}
 * An object containing the response status, fetched data (if successful), or an error message.
 */
export const postQuery = async (url, query, variables = {}) => {
  try {
    const apiKey = await getApiKey("vbgo");
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey.data,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      console.error(`HTTP error! status: ${response.status}`);
      return { response: response.status, error: await response.text() };
    }

    const data = await response.json();
    AppSettings.setState({ connectionError: false });
    return { response: 200, data: data.data };
  } catch (error) {
    console.error("Error in postQuery:", error);
    AppSettings.setState({ connectionError: true });
    return { response: 400, error };
  }
};

/**
 * Performs general post request.
 * @async
 * @function post
 * @param {string} url - The api endpoint to send the request to.
 * @param {Object} body - The request body for the POST request.
 * @returns {Promise<{response: number, data?: any, error?: any}>}
 * An object containing the response status, fetched data (if successful), or an error message.
 */
export const post = async (url, body) => {
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.error(`HTTP error! status: ${response.status}`);
      return { response: response.status, error: await response.text() };
    }

    const data = await response.json();
    AppSettings.setState({ connectionError: false });
    return { response: 200, data: data };
  } catch (error) {
    console.error("Error in post:", error);
    AppSettings.setState({ connectionError: true });
    return { response: 400, error };
  }
};

/**
 * Fetches API key by name
 * @async
 * @function getApiKey
 * @param {string} name - The name of the api key to fetch
 * @returns {Promise<{response: number, data?: any, error?: any}>}
 */
export const getApiKey = async (name) => {
  try {
    const apiKeyResponse = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apikey/${name}`);
    if (!apiKeyResponse.ok) {
      console.error(` ❌ Failed to fetch API key! status: ${apiKeyResponse.status}`);
      return { response: apiKeyResponse.status, error: await apiKeyResponse.text() };
    }
    const apiKey = await apiKeyResponse.json();
    return { response: 200, data: apiKey.key };
  } catch (error) {
    console.error("Error fetching API key:", error);
    return { response: 400, error };
  }
};

/**
 * Subscribes a device to web push notifications for a given topic.
 * Sends the FCM token and topic to the backend which registers the
 * device for topic-based push delivery.
 *
 * @async
 * @function subscribeToWebPushNotifications
 * @param {string} topic - The notification topic/channel name (e.g., "vbgo-dev")
 * @param {string} token - The FCM device registration token
 * @returns {Promise<Object|undefined>} The subscription response data on success, or undefined on failure
 */
export const subscribeToWebPushNotifications = async (topic, token) => {
  try {
    const result = await post(`${process.env.REACT_APP_API_ENDPOINT}/subscribe-push`, {
      token: token,
      topic: topic,
    });

    if (result.response === 200) {
      return result.data;
    } else {
      console.error("Failed to subscribe to web push notifications");
    }
  } catch (error) {
    console.error("Error subscribing to web push notifications", error);
    return { response: 400, error };
  }
};

/**
 * Fetches an API key for the push notification system.
 * Wraps getApiKey to return just the key string (or null/undefined on failure),
 * providing a simplified interface for the push notification submodule.
 *
 * @async
 * @function getPushNotificationApiKey
 * @param {string} name - The name of the API key to fetch (e.g., "vapid")
 * @returns {Promise<string|null|undefined>} The key string on success, null on non-200 response, undefined on unexpected error
 */
export const getPushNotificationApiKey = async (name) => {
  try {
    const response = await getApiKey(name);
    if (response.response === 200) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Failed to get ${name} key`);
  }
};

/**
 * Fetches nearby bus stops for the given coordinates.
 * Returns the bus stop data.
 *
 * @async
 * @function fetchNearbyBusStops
 * @param {number} latitude - The latitude coordinate of the location
 * @param {number} longitude - The longitude coordinate of the location
 * @returns {Promise<Array>} Array of bus stop objects, or empty array on error
 */
export const fetchNearbyBusStops = async (latitude, longitude) => {
  const url = `${process.env.REACT_APP_API_ENDPOINT}/bus-stops?latitude=${latitude}&longitude=${longitude}`;
  const result = await getQuery(url);
  if (result.response === 200) {
    return result.data.results;
  } else {
    return [];
  }
};
